import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Eye, EyeOff, AlertCircle, CheckCircle2, Loader2, KeyRound, Mail } from 'lucide-react';
import { validateResetToken, setPassword } from '../api/auth.js';

/* ── Brand panel — same as Login ─────────────────────────── */
function BrandPanel() {
  return (
    <div
      className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden"
      style={{ background: 'linear-gradient(145deg, #009ee3 0%, #0062cc 60%, #003f8a 100%)' }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10"
           style={{ background: 'radial-gradient(circle, white, transparent)' }} />
      <div className="absolute bottom-0 -left-16 w-72 h-72 rounded-full opacity-10"
           style={{ background: 'radial-gradient(circle, white, transparent)' }} />

      <div className="relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center border border-white/30">
            <span className="text-white font-bold text-sm">MP</span>
          </div>
          <span className="text-white font-bold text-xl tracking-tight">McaMP</span>
        </div>
      </div>

      <div className="relative z-10 space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur border border-white/25 flex items-center justify-center">
          <KeyRound size={26} className="text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white leading-tight">
          Activación<br />de cuenta
        </h1>
        <p className="text-blue-100 text-sm leading-relaxed max-w-xs">
          Establecé tu contraseña personal para acceder al panel de administración.
          El enlace expira a las 72 horas.
        </p>
      </div>

      <div className="relative z-10">
        <p className="text-xs text-blue-200/60">© {new Date().getFullYear()} MCAnet · mp-pos-api</p>
      </div>
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────── */
export default function Activate() {
  const navigate      = useNavigate();
  const [params]      = useSearchParams();
  const token         = params.get('token') || '';

  const [status,   setStatus]   = useState('loading'); // loading | valid | invalid | done
  const [reason,   setReason]   = useState('');
  const [password, setPassword_] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [formErr,  setFormErr]  = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setReason('No hay token en el enlace. Revisá el correo de bienvenida.');
      return;
    }
    validateResetToken(token)
      .then((res) => {
        if (res.valid) setStatus('valid');
        else { setStatus('invalid'); setReason(res.reason); }
      })
      .catch(() => { setStatus('invalid'); setReason('No se pudo validar el enlace. Intentá de nuevo.'); });
  }, [token]);

  const mutation = useMutation({
    mutationFn: ({ token: t, password: p }) => setPassword({ token: t, password: p }),
    onSuccess: () => {
      setStatus('done');
      setTimeout(() => navigate('/login?activated=1', { replace: true }), 2500);
    },
    onError: (e) => {
      const msg = e?.response?.data?.error;
      setFormErr(typeof msg === 'string' ? msg : 'Error al establecer la contraseña. Intentá de nuevo.');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormErr('');
    if (password.length < 8) return setFormErr('La contraseña debe tener al menos 8 caracteres.');
    if (password !== confirm) return setFormErr('Las contraseñas no coinciden.');
    mutation.mutate({ token, password });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: 'var(--bg)' }}>

      <BrandPanel />

      <div className="flex flex-col items-center justify-center px-8 py-12">

        {/* Mobile logo */}
        <div className="flex items-center gap-2 mb-10 lg:hidden">
          <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
            <span className="text-white font-bold text-sm">MP</span>
          </div>
          <span className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>McaMP</span>
        </div>

        <div className="w-full max-w-sm">

          {/* Loading */}
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-12">
              <Loader2 size={32} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Verificando enlace…</p>
            </div>
          )}

          {/* Invalid token */}
          {status === 'invalid' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Enlace inválido</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>No se puede activar la cuenta</p>
              </div>
              <div className="flex items-start gap-3 px-4 py-4 rounded-xl text-sm
                              bg-red-400/10 border border-red-400/20 text-red-400">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{reason}</span>
              </div>
              <div className="flex items-start gap-3 px-4 py-4 rounded-xl text-sm border"
                   style={{ background: 'var(--bg-elevated)', borderColor: 'var(--border)', color: 'var(--text-secondary)' }}>
                <Mail size={16} className="shrink-0 mt-0.5" style={{ color: 'var(--text-muted)' }} />
                <span>Contactá al administrador del sistema para que te reenvíe el correo de activación.</span>
              </div>
            </div>
          )}

          {/* Success */}
          {status === 'done' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 flex items-center justify-center">
                  <CheckCircle2 size={24} className="text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Contraseña establecida</h2>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Redirigiendo al login…</p>
                </div>
              </div>
            </div>
          )}

          {/* Valid token — password form */}
          {status === 'valid' && (
            <div className="space-y-1">
              <div className="mb-8">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  Establecé tu contraseña
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                  Mínimo 8 caracteres. No podrás reutilizar este enlace.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" noValidate>

                {/* New password */}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                    Nueva contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Mínimo 8 caracteres"
                      value={password}
                      onChange={(e) => setPassword_(e.target.value)}
                      className="w-full px-4 py-2.5 pr-11 rounded-xl text-sm border transition-all
                                 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      style={{ background: 'var(--input-bg)', borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: 'var(--text-muted)' }}
                      tabIndex={-1}
                    >
                      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Confirm */}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                    Confirmar contraseña
                  </label>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Repetí la contraseña"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl text-sm border transition-all
                               focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                    style={{ background: 'var(--input-bg)', borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}
                  />
                </div>

                {/* Error */}
                {formErr && (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-red-400
                                  bg-red-400/10 border border-red-400/20">
                    <AlertCircle size={15} className="shrink-0" />
                    {formErr}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!password || !confirm || mutation.isPending}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl
                             text-sm font-semibold text-white transition-all
                             disabled:opacity-50 disabled:cursor-not-allowed
                             hover:brightness-110 active:scale-[0.98]"
                  style={{ background: 'rgb(var(--accent-rgb))' }}
                >
                  {mutation.isPending
                    ? <><Loader2 size={16} className="animate-spin" />Guardando…</>
                    : 'Activar cuenta'}
                </button>

              </form>
            </div>
          )}

          <p className="text-xs text-center mt-8" style={{ color: 'var(--text-muted)' }}>
            McaMP · Panel de administración
          </p>
        </div>
      </div>
    </div>
  );
}
